// apps/client/src/hooks/useReferralPartners.ts
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface ReferralPartner {
  referrer: string;
  loanCount: number;
  totalUpfrontPaidCents: number;
  totalTrailPaidCents: number;
  firstReferralDate: string | null;
  lastReferralDate: string | null;
  isInactive: boolean;
}

export interface ReferralTotals {
  totalPartners: number;
  activePartners: number;
  inactivePartners: number;
  totalUpfrontSpentCents: number;
  totalTrailSpentCents: number;
  totalSpentCents: number;
  averagePerPartnerCents: number;
}

interface UseReferralPartnersParams {
  companyId: string;
  periodStart?: string;
  periodEnd?: string;
  entityCode?: string;
}

interface UseReferralPartnersReturn {
  partners: ReferralPartner[];
  totals: ReferralTotals | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch Referral Partner Report data
 * Surfaces existing referrer column data from raw_commissions
 */
export function useReferralPartners({
  companyId,
  periodStart,
  periodEnd,
  entityCode,
}: UseReferralPartnersParams): UseReferralPartnersReturn {
  const [partners, setPartners] = useState<ReferralPartner[]>([]);
  const [totals, setTotals] = useState<ReferralTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('[useReferralPartners] Fetching report:', { companyId, periodStart, periodEnd });

        const getReport = httpsCallable<
          { companyId: string; periodStart?: string; periodEnd?: string; entityCode?: string },
          { partners: ReferralPartner[]; totals: ReferralTotals }
        >(functions, 'getReferralPartnerReport');

        const result = await getReport({ companyId, periodStart, periodEnd, entityCode });

        console.log('[useReferralPartners] Report received:', {
          partnerCount: result.data.partners.length,
          totalSpent: result.data.totals.totalSpentCents / 100,
        });

        setPartners(result.data.partners);
        setTotals(result.data.totals);
      } catch (err: any) {
        console.error('[useReferralPartners] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch referral partner report');
        setPartners([]);
        setTotals(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [companyId, periodStart, periodEnd, entityCode]);

  return { partners, totals, loading, error };
}
